import { ApolloError } from '@apollo/client'
import { Button, FormHelperText, Stack, Typography } from '@mui/material'
import set from 'lodash/set'
import { useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { TextInputFieldProps } from 'utoniq-ui'

import { EventCollectionBoxFragment } from '@/clients/utoniq-core/schema'
import {
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { dayjs } from '@/lib/dayjs'

import { TextInputCell } from './components/common'
import {
  PickupTime,
  PickupTimeError,
  PickupTimesEditForm,
} from './components/PickupTimesEditForm'

type EventCollectionReceiveTimeEditFormProps = {
  eventCollection: EventCollectionBoxFragment | undefined
  days: string[]
  onSubmit: (
    values: EventCollectionReceiveTimeValue & {
      pickupTimes: PickupTime[]
    }
  ) => Promise<void>
  updatedErrors?: ApolloError
  submitting: boolean
  maxValidMinutes: number
}

export type EventCollectionReceiveTimeValue = {
  validMinutes: string
  pickupLocation: string
  purchaseLimitPerPickup?: number
}

/**
 * @description
 * イベント受け渡し情報の編集フォーム
 * このファイルでは以下のデータを管理する
 * - 受け渡し場所、有効時間などの汎用的な情報(react-hook-formのスキーマ)
 * - 受け渡し時間の情報(state)。PickupTimesEditFormコンポーネントに渡す
 *
 * react-hook-formのhandleSubmitを実行した時に、このファイルのスキーマと、受け渡し情報stateを結合してonSubmitアクションを実行する
 */
export const EventCollectionReceiveTimeEditForm: Organism<EventCollectionReceiveTimeEditFormProps> =
  ({ eventCollection, days, onSubmit, submitting, maxValidMinutes }) => {
    const { showError } = useMessage()
    const schema: UseFormProps['schema'] = o => ({
      pickupLocation: o
        .string()
        .label(tLabel('consoleEventCollectionReceiveTimeEdit.pickupLocation')),
      validMinutes: o
        .number()
        .min(10)
        .max(maxValidMinutes)
        .label(tLabel('consoleEventCollectionReceiveTimeEdit.validMinutes')),
      purchaseLimitPerPickup: o
        .number()
        .min(1)
        .label(
          tLabel('consoleEventCollectionReceiveTimeEdit.purchaseLimitPerPickup')
        )
        .allow('')
        .optional(),
    })

    const { t } = useTranslation('consoleEventCollectionReceiveTimeEdit')
    const {
      handleSubmit,
      formState: { errors },
      register,
      reset,
      setError,
    } = useForm<EventCollectionReceiveTimeValue>({
      schema,
      defaultValues,
    })

    const [pickupTimeErrors, setPickupTimeErrors] = useState<PickupTimeError[]>(
      []
    )
    const [pickupTimes, setPickupTimes] = useState<PickupTime[]>(
      days?.map(day => ({
        pickupDate: day,
        pickupTime:
          eventCollection?.pickupTime
            ?.filter(({ options }) => DateTime.formatDate(options.time) === day)
            .map(({ options }) => ({
              options: {
                time: (DateTime.formatDate(options.time, 'HH:mm') ??
                  '') as string,
                availableUserNumber: options.availableUserNumber ?? 0,
                currentUserNumber: options.currentUserNumber ?? 0,
              },
            })) ?? [],
      }))
    )

    useEffect(() => {
      reset({
        validMinutes: String(eventCollection?.validMinutes || ''),
        pickupLocation: eventCollection?.pickupLocation || '',
        purchaseLimitPerPickup:
          eventCollection?.purchaseLimitPerPickup || undefined,
      })
    }, [eventCollection])

    const onHandleSubmit = handleSubmit(async values => {
      await onSubmit({
        ...values,
        pickupTimes,
      }).catch(errors => {
        showError(errors)
        if (errors instanceof ApolloError) {
          let path
          const pickupTimeErrs: PickupTimeError[] = []
          errors.graphQLErrors.forEach(({ extensions }) => {
            extensions?.exception?.errors?.forEach(
              ({
                errorCode,
                userMessage,
                pickupTime,
              }: {
                errorCode: string
                userMessage: string
                pickupTime: string
              }) => {
                switch (errorCode) {
                  case 'INVALID_VALID_MINUTES':
                    setError('validMinutes', { message: userMessage })
                    break
                  case 'INVALID_DATE':
                    path = getPickupTimePath(pickupTime, pickupTimes)
                    if (path) {
                      set(pickupTimeErrs, path, userMessage)
                    }
                    break
                  default:
                }
              }
            )
          })
          setPickupTimeErrors(pickupTimeErrs)
        }
      })
    })

    return (
      <OrganismFrame>
        {/* 受け渡し情報。受け取り場所 */}
        <PickupLocationInputCell
          inputProps={{ type: 'text', ...register('pickupLocation') }}
          error={errors.pickupLocation}
        />
        {/* 受け渡し情報。受け取り人数制限 */}
        <PurchaseLimitInputCellInputCell
          inputProps={{ type: 'tel', ...register('purchaseLimitPerPickup') }}
          error={errors.purchaseLimitPerPickup}
        />
        {/* 受け渡し情報。有効時間 */}
        <TicketValidMinutesInputCell
          inputProps={{ type: 'number', ...register('validMinutes') }}
          error={errors.validMinutes}
        />
        {/* 受け渡し時間stateを編集するフォーム */}
        <PickupTimesEditForm
          pickupTimes={pickupTimes}
          setPickupTimes={setPickupTimes}
          days={days}
          pickupTimeErrors={pickupTimeErrors}
        />
        <Stack alignItems="center" sx={{ py: 6 }}>
          <Button
            size="large"
            onClick={onHandleSubmit}
            loading={submitting}
            disabled={submitting || !days.length}
            sx={{ width: 212 }}
          >
            {t('register')}
          </Button>
        </Stack>
      </OrganismFrame>
    )
  }

type PickupLocationInputCellProps = {
  inputProps: Omit<TextInputFieldProps, 't'>
  error?: FieldErrors
}

const PickupLocationInputCell: Cell<PickupLocationInputCellProps> = ({
  inputProps,
  error,
}) => {
  const { t } = useTranslation('consoleEventCollectionReceiveTimeEdit')
  return (
    <CellFrame sx={{ pt: 4, pb: 3 }}>
      <TextInputCell
        {...inputProps}
        textFieldProps={{
          placeholder: t('pickupLocationPlaceHolder'),
          helperText: undefined,
          sx: { mb: 0 },
        }}
      />
      {error && <FormHelperText error>{error.message}</FormHelperText>}
    </CellFrame>
  )
}

type PurchaseLimitInputCellInputCellProps = {
  inputProps: Omit<TextInputFieldProps, 't'>
  error?: FieldErrors
}

const PurchaseLimitInputCellInputCell: Cell<PurchaseLimitInputCellInputCellProps> =
  ({ inputProps, error }) => {
    const { t } = useTranslation('consoleEventCollectionReceiveTimeEdit')
    return (
      <CellFrame sx={{ pt: 4, pb: 3 }}>
        <Typography variant="h5">
          {t('purchaseLimitPerPickup', '受け渡し商品数の上限')}
        </Typography>
        <Typography variant="body2" sx={{ pt: 1, color: 'text.secondary' }}>
          {t(
            'purchaseLimitPerPickupNote',
            'ユーザーは受け取りごとにこの上限を超えて商品を購入できなくなります。'
          )}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextInputCell
            {...inputProps}
            textFieldProps={{
              placeholder: t('purchaseLimitPerPickupPlaceHolder'),
              sx: { minWidth: 90, width: 90, mb: 0 },
              helperText: undefined,
            }}
          />
          <Typography variant="subtitle1" sx={{ pt: 2 }}>
            {t('piece')}
          </Typography>
        </Stack>
        {error && <FormHelperText error>{error.message}</FormHelperText>}
      </CellFrame>
    )
  }

type TicketValidMinutesInputCellProps = {
  inputProps: Omit<TextInputFieldProps, 't'>
  error?: FieldErrors
}
const TicketValidMinutesInputCell: Cell<TicketValidMinutesInputCellProps> = ({
  inputProps,
  error,
}) => {
  const { t } = useTranslation('consoleEventCollectionReceiveTimeEdit')
  return (
    <CellFrame sx={{ py: 4 }}>
      <Typography variant="h5">
        {t('validMinutes', '受け渡しの有効時間')}
      </Typography>
      <Typography variant="body2" sx={{ pt: 1, color: 'text.secondary' }}>
        {t(
          'validMinutesNote',
          '受け渡し時間を設定すれば、購入者は設定時間外で受け取り時間を指定できなくなります。'
        )}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <TextInputCell
          {...inputProps}
          textFieldProps={{
            placeholder: '30',
            sx: { minWidth: 90, width: 90, mb: 0 },
            helperText: undefined,
          }}
        />
        <Typography variant="subtitle1" sx={{ pt: 2 }}>
          {t('point', '分')}
        </Typography>
      </Stack>
      {error && <FormHelperText error>{error.message}</FormHelperText>}
    </CellFrame>
  )
}

const defaultValues: EventCollectionReceiveTimeValue = {
  validMinutes: '',
  pickupLocation: '',
}

function getPickupTimePath(
  pickupTime: string,
  pickupTimes: PickupTime[]
): string {
  const inputPickupTime = dayjs(pickupTime)
  const indexPickupDate = pickupTimes.findIndex(
    p => p.pickupDate === inputPickupTime.format('YYYY/MM/DD')
  )
  if (indexPickupDate < 0) {
    return ''
  }
  const indexPickupTime = pickupTimes[indexPickupDate].pickupTime?.findIndex(
    p => p.options.time === inputPickupTime.format('HH:mm')
  )
  if (indexPickupTime < 0) {
    return ''
  }
  return `${indexPickupDate}.pickupTime.${indexPickupTime}.options.time`
}
